import type { CustomMetricsCustomPortfolioExAnteMetricsEnum, PortfolioExAnteMetric } from "@eu.mdotm.api/sphere";
import { runWithErrorReporting } from "$root/api/error-reporting";
import { WidgetDownloadTrigger } from "$root/components/DropdownMenuTrigger";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { workspaceRepository } from "$root/functional-areas/workspace/repository";
import { isAsOfReadable } from "$root/pages/PortfolioDetails/as-of/useAsOfSnapshot";
import { WidgetStatus, portfolioWidgetMissingDataReason } from "$root/pages/PortfolioDetails/PortfolioWidgetStatus";
import { type ContextContent, downloadContentDisposition, qualifier, withContext } from "$root/utils";
import { PortfolioContext } from "$root/widgets-architecture/contexts/portfolio";
import { type OrderBy, Row, Text } from "@mdotm/shadcn/legacy/components";
import { DropdownMenu } from "@mdotm/shadcn/mdotm/components";
import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import { Map } from "immutable";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	type PortfolioMetricsRow,
	PortfolioMetricsBase,
	ReapplySphereMetricsIconTooltip,
} from "../PortfolioMetrics/PortfolioMetricsBase";
import { classificationMetricDisplay, decimalPlaces, unitOfMeasurement } from "$root/functional-areas/metrics";
import { InfoTooltip } from "$root/widgets-architecture/layout/WidgetsMapper/InfoTooltip";
import { buildWidgetExportConfig } from "../widget-export";
import { formatCurrencyAmount } from "$root/utils/currency";

const ExanteMetricsBlock = (props: ContextContent<typeof PortfolioContext>) => {
	const uuid = props.portfolio?.uuid;
	const enhanced = props.enhanced;
	const benchmarkId = props.selectedBenchmark ?? props.portfolio?.primaryBenchmarkIdentifier ?? null;
	const { asOf, asOfSnapshot } = props;
	const quantityMode = Boolean(props.portfolio?.quantityMode);
	const baseCurrency = props.portfolio?.baseCurrency;
	const { t } = useTranslation();

	// The monetary value under VaR and max drawdown is shown on a quantities portfolio only. Gated here, at
	// render, so a change of mode never changes the metrics query.
	const formatMonetary = useMemo(
		() => (quantityMode ? (value: number) => formatCurrencyAmount(value, baseCurrency) : undefined),
		[quantityMode, baseCurrency],
	);

	// Mirror the table's column sort so the export reproduces it. Defaults to none (the data provider's
	// natural metric order); once the user sorts a column the export carries that same sort.
	const [orderBy, setOrderBy] = useState<OrderBy[]>([]);

	const isCalculating = useMemo(
		() =>
			(props.portfolio?.action === "UPLOAD" || props.portfolio?.action === "CREATION") &&
			props.portfolio?.status === "CALCULATING",
		[props.portfolio?.action, props.portfolio?.status],
	);

	// Download — mirrors the visible table's percentages via the smart-workspace `investmentPortfolioExAnteMetric`
	// data provider. The monetary values under VaR and max drawdown are not in the export.
	const exportExanteMetrics = async (investmentUuid: string) => {
		const config = buildWidgetExportConfig(
			"ExanteMetricsBlock",
			{
				investmentUuid,
				benchmarkIdentifier: benchmarkId ?? "",
				source: enhanced ? "PROPOSAL" : "CURRENT",
				proposalDate: props.portfolio?.modificationDate,
				sort: orderBy,
			},
			{ asOf },
		);
		await runWithErrorReporting(
			async () => {
				const file = await workspaceRepository.exportWorkspaceDataRaw.fetchQuery(config);
				await downloadContentDisposition(file);
			},
			{
				area: "portfolio",
				attemptedOperation: {
					message: "export ex-ante portfolio metrics",
					payload: JSON.stringify(config),
				},
			},
		);
	};

	const exportExanteMetricsRef = useUnsafeUpdatedRef(exportExanteMetrics);

	const downloadActionHeader = useMemo(
		() =>
			uuid ? (
				<DropdownMenu
					Trigger={WidgetDownloadTrigger}
					extraTriggerProps={{ "data-qualifier": qualifier.widgets.portfolioExAnteMetric.downloadButton }}
					actions={[
						{
							icon: "xls",
							onClickAsync: async () => {
								await exportExanteMetricsRef.current(uuid);
							},
							label: t("EXANTE_METRICS.DOWNLOAD_TITLE"),
							"data-qualifier": qualifier.widgets.portfolioExAnteMetric.downloadItem,
						},
					]}
				/>
			) : null,
		[uuid, t, exportExanteMetricsRef],
	);

	return (
		<PortfolioMetricsBase
			proposalDate={props.portfolio?.modificationDate}
			hasBenchmark={Boolean(benchmarkId)}
			actionHeader={downloadActionHeader}
			formatMonetary={formatMonetary}
			orderBy={orderBy}
			onOrderByChange={setOrderBy}
			title={({ lastImportDate, hasCustomMetrics }) => (
				<Row alignItems="center" gap={8}>
					<Text
						type="Body/XL/Bold"
						title={t("EXANTE_METRICS.TITLE")}
						className="truncate"
						data-qualifier={qualifier.widgets.portfolioExAnteMetric.name}
					>
						{t("EXANTE_METRICS.TITLE")}
					</Text>
					<InfoTooltip>{t("EXANTE_METRICS.TOOLTIP")}</InfoTooltip>
					{lastImportDate && hasCustomMetrics && (
						<ReapplySphereMetricsIconTooltip lastUpdate={lastImportDate} portfolio={props.portfolio} />
					)}
				</Row>
			)}
			noDataText={t("EXANTE_METRICS.NO_DATA")}
			enabled={isAsOfReadable(asOf, asOfSnapshot)}
			queryKey={[portfolioRepository.exanteMetrics.fullQueryKey, uuid, benchmarkId, enhanced, asOf]}
			metricsProvider={async () => {
				const exanteMetrics = await portfolioRepository.exanteMetrics.fetchQuery(uuid, {
					enhanced,
					benchmarkId,
					asOf,
				});

				if (!exanteMetrics) {
					return {
						data: undefined,
						// A stored snapshot that holds no ex-ante metrics for a past date will never gain them, so the
						// live reasons — which promise the calculation is on its way — give way to EMPTY, the status
						// the widget bases turn into the date-specific wall (MDOT-24860).
						widgetStatus: asOf
							? WidgetStatus.EMPTY
							: portfolioWidgetMissingDataReason(props.portfolio, "ExanteContributionVolatility"),
					};
				}

				const {
					portfolioExAnteMetrics,
					customPortfolioExAnteMetrics,
					metadataLastUpdate,
					scorePortfolioExAnteMetrics,
				} = exanteMetrics;
				if (!portfolioExAnteMetrics || isCalculating) {
					return {
						data: undefined,
						widgetStatus: asOf
							? WidgetStatus.EMPTY
							: portfolioWidgetMissingDataReason(props.portfolio, "ExanteContributionVolatility"),
					};
				}

				const customMetricsMap = Map<CustomMetricsCustomPortfolioExAnteMetricsEnum, PortfolioExAnteMetric>(
					customPortfolioExAnteMetrics?.flatMap((metric) => (metric.type ? [[metric.type, metric]] : [])),
				);

				const metrics = portfolioExAnteMetrics?.flatMap((metric): [PortfolioMetricsRow] | [] =>
					metric.type
						? [
								{
									label: t(`EXANTE_METRICS.METRICS.${metric.type}`),
									name: metric.type,
									visible: true,
									current: customMetricsMap.get(metric.type)?.current ?? metric.current,
									benchmark: customMetricsMap.get(metric.type)?.benchmark ?? metric.benchmark,
									proposal: metric.proposal,
									// An imported current percentage is not the one the backend's value was computed from.
									monetary: {
										current: customMetricsMap.get(metric.type)?.current != null ? undefined : metric.currentValue,
										proposal: metric.proposalValue,
									},
									isCurrentCustom: customMetricsMap.get(metric.type)?.current != null,
									isBenchmarkCustom: customMetricsMap.get(metric.type)?.benchmark != null,
									unitOfMeasurement: unitOfMeasurement.exAnteMetric[metric.type] ?? "",
									decimalPlaces: decimalPlaces.exAnteMetric[metric.type],
								},
							]
						: [],
				);

				(scorePortfolioExAnteMetrics ?? []).forEach((metric) => {
					if (metric.metadata?.classificationName) {
						const { unitOfMeasurement, scale } = classificationMetricDisplay(metric.metadata.classificationName);
						// Rates arrive as fractions; scale to match the "%" label (no-op scale of 1 otherwise).
						const scaled = (value: number | undefined) => (value == null ? value : value * scale);
						metrics.push({
							label: `Avg. ${metric.metadata?.classificationName}`,
							name: metric.metricParameters?.classificationUuid as any,
							visible: true,
							current: scaled(metric.current),
							benchmark: scaled(metric.benchmark),
							proposal: scaled(metric.proposal),
							isCurrentCustom: false,
							isBenchmarkCustom: false,
							unitOfMeasurement,
							decimalPlaces: 2,
						});
					}
				});
				return {
					data: { metrics, lastImportDate: metadataLastUpdate, hasCustomMetrics: customMetricsMap.size > 0 },
					widgetStatus: WidgetStatus.READY,
				};
			}}
		/>
	);
};

export default withContext(PortfolioContext)(ExanteMetricsBlock);
